import { DOCUMENT } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { OurAuthService } from 'app/core/auth/auth.service';
import { environment } from 'environments/environment';
import { distinctUntilChanged, filter, map, Observable, Subject } from 'rxjs';

/** Frame shape on the backend `userEvents` multiplex WebSocket. */
export interface UserEventMessage {
    event: string;
    data: unknown;
}

/**
 * Shared client for the backend `userEvents` WebSocket
 * (`data_source/user-events-socket?token=...`). One connection multiplexes
 * many backend events as `{ event, data }` frames (e.g. `table-description`,
 * `activities-updated`); the server replays the latest snapshot per event on
 * connect and on any inbound message.
 *
 * Consumers call `on('<event>')`. The socket is opened lazily on the first
 * subscriber, shared across all events, auto-reconnects while at least one
 * subscriber exists, and closes when the last subscriber unsubscribes.
 * Logout (`isLoggedIn$` → false) force-closes the socket and cancels reconnect
 * so table-description / activities events cannot keep notifying a signed-out session.
 *
 * ## Surviving a dropped connection
 *
 * Measured on the dev server, this socket does not stay up for hours: connections
 * were being closed roughly every minute and rebuilt, which is normal for a long
 * idle WebSocket crossing home routers, mobile NAT and corporate proxies. So the
 * interesting case is not "does it connect" but "what happens to the events that
 * were emitted while it was down", and three things here answer that:
 *
 *  - a client heartbeat, so an idle connection is never silently reaped as dead;
 *  - `reconnected$`, so a consumer can reconcile over HTTP after any gap rather
 *    than trusting that it missed nothing;
 *  - backoff with jitter plus a wake on `online` / tab-visible, so a client comes
 *    back promptly without a fleet of tabs retrying in lockstep.
 */
@Injectable({ providedIn: 'root' })
export class UserEventsSocketService {
    private _authService = inject(OurAuthService);
    private readonly _document = inject(DOCUMENT);

    private socket: WebSocket | null = null;
    private readonly messages$ = new Subject<UserEventMessage>();
    private readonly connected$ = new Subject<void>();
    private subscriberCount = 0;
    private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
    /** Consecutive failed/dropped connections; drives the backoff and resets on open. */
    private reconnectAttempts = 0;
    /** True once a connection has been established, so the first open is not a "reconnect". */
    private hasConnectedOnce = false;
    private wakeListenersBound = false;

    private readonly baseReconnectDelayMs = 1000;
    private readonly maxReconnectDelayMs = 15000;
    /**
     * Below the shortest idle timeout worth defending against (the server pings at
     * 30s; this covers the client-to-server direction, which some intermediaries
     * count separately). The server answers any inbound frame with a full snapshot,
     * so the heartbeat doubles as a periodic catch-up for anything dropped on the wire.
     */
    private readonly heartbeatIntervalMs = 25000;

    constructor() {
        this._authService.isLoggedIn$
            .pipe(distinctUntilChanged())
            .subscribe((loggedIn: boolean): void => {
                if (!loggedIn) {
                    this.disconnect();
                }
            });
    }

    /** Stream of `data` payloads for one event name. Subscribing keeps the socket alive. */
    on(eventName: string): Observable<unknown> {
        return new Observable<unknown>((subscriber) => {
            this.subscriberCount++;
            this.bindWakeListeners();
            this.ensureSocket();
            const inner = this.messages$
                .pipe(
                    filter(message => message.event === eventName),
                    map(message => message.data),
                )
                .subscribe(subscriber);
            return (): void => {
                inner.unsubscribe();
                this.subscriberCount = Math.max(0, this.subscriberCount - 1);
                if (this.subscriberCount === 0) {
                    this.teardownSocket();
                }
            };
        });
    }

    /**
     * Emits after every reconnect that follows a dropped connection (never on the
     * first connect of a session).
     *
     * A consumer that only reacts to frames cannot tell "nothing happened" from "I
     * was not listening", and those are the same thing on the wire. This says the
     * second one happened, which is the cue to reconcile from the server instead of
     * assuming the local view is still current.
     */
    get reconnected$(): Observable<void> {
        return this.connected$.asObservable();
    }

    /** Ask the server to replay the latest snapshot of every tracked event. */
    requestSnapshot(): void {
        if (this.socket?.readyState === WebSocket.OPEN) {
            try {
                this.socket.send('refresh');
            } catch {
                // Socket lifecycle handlers own reconnect; ignore send errors.
            }
        }
    }

    /**
     * Force-close the multiplex socket and cancel reconnect.
     * Called on logout; safe to call when already disconnected.
     */
    disconnect(): void {
        this.teardownSocket();
    }

    private ensureSocket(): void {
        if (
            this.socket &&
            (this.socket.readyState === WebSocket.OPEN ||
                this.socket.readyState === WebSocket.CONNECTING)
        ) {
            return;
        }

        const token = this._authService.accessToken;
        if (!token) {
            return;
        }

        const url = `${environment.socketLink}data_source/user-events-socket?token=${encodeURIComponent(token)}`;
        const socket = new WebSocket(url);
        this.socket = socket;

        socket.onopen = (): void => {
            if (this.socket !== socket) {
                return;
            }
            this.reconnectAttempts = 0;
            this.startHeartbeat();
            this.requestSnapshot();
            if (this.hasConnectedOnce) {
                this.connected$.next();
            }
            this.hasConnectedOnce = true;
        };

        socket.onmessage = (event: MessageEvent): void => {
            for (const message of this.parseFrames(event.data)) {
                this.messages$.next(message);
            }
        };

        socket.onclose = (): void => {
            if (this.socket === socket) {
                this.socket = null;
                this.stopHeartbeat();
                if (this.subscriberCount > 0 && this._authService.accessToken) {
                    this.scheduleReconnect();
                }
            }
        };

        socket.onerror = (): void => {
            // onclose follows and owns reconnect scheduling.
        };
    }

    /**
     * Backoff with jitter: 1s, 2s, 4s, 8s, capped at 15s, each shortened by up to 30%.
     *
     * Capped because this connection is how a page learns that a run finished, and a
     * client that has backed off to minutes is a client showing stale state. Jittered
     * because every tab of an app that reconnects on a fixed delay reconnects at the
     * same instant, which turns one server restart into a synchronised stampede.
     */
    private scheduleReconnect(): void {
        if (this.reconnectTimer) {
            return;
        }
        const backoff = Math.min(
            this.baseReconnectDelayMs * 2 ** this.reconnectAttempts,
            this.maxReconnectDelayMs,
        );
        this.reconnectAttempts++;
        const delay = Math.round(backoff * (0.7 + Math.random() * 0.3));
        this.reconnectTimer = setTimeout(() => {
            this.reconnectTimer = null;
            if (this.subscriberCount > 0 && this._authService.accessToken) {
                this.ensureSocket();
            }
        }, delay);
    }

    /** Retry now rather than waiting out a backoff that was scheduled while offline / hidden. */
    private reconnectNow(): void {
        if (this.subscriberCount === 0 || !this._authService.accessToken) {
            return;
        }
        if (this.socket) {
            return;
        }
        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
        }
        this.reconnectAttempts = 0;
        this.ensureSocket();
    }

    /**
     * Coming back online, or returning to a backgrounded tab, are the two moments a
     * client is most likely to be holding a view built before a gap. Both retry
     * immediately; the reconnect itself pulls a fresh snapshot and fires `reconnected$`.
     */
    private bindWakeListeners(): void {
        if (this.wakeListenersBound || typeof window === 'undefined') {
            return;
        }
        this.wakeListenersBound = true;
        window.addEventListener('online', () => this.reconnectNow());
        this._document.addEventListener('visibilitychange', () => {
            if (this._document.visibilityState === 'visible') {
                this.reconnectNow();
                // Already-open sockets can still have missed a frame while hidden.
                this.requestSnapshot();
            }
        });
    }

    private startHeartbeat(): void {
        this.stopHeartbeat();
        this.heartbeatTimer = setInterval(
            () => this.requestSnapshot(),
            this.heartbeatIntervalMs,
        );
    }

    private stopHeartbeat(): void {
        if (this.heartbeatTimer) {
            clearInterval(this.heartbeatTimer);
            this.heartbeatTimer = null;
        }
    }

    private teardownSocket(): void {
        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
        }
        this.stopHeartbeat();
        this.reconnectAttempts = 0;
        const socket = this.socket;
        this.socket = null;
        if (socket) {
            try {
                socket.close();
            } catch {
                // Already closed.
            }
        }
    }

    private parseFrames(raw: unknown): UserEventMessage[] {
        if (typeof raw !== 'string' || !raw.trim()) {
            return [];
        }
        let parsed: unknown;
        try {
            parsed = JSON.parse(raw);
        } catch {
            return [];
        }
        const candidates = Array.isArray(parsed) ? parsed : [parsed];
        const frames: UserEventMessage[] = [];
        for (const candidate of candidates) {
            if (
                candidate &&
                typeof candidate === 'object' &&
                typeof (candidate as { event?: unknown }).event === 'string'
            ) {
                frames.push({
                    event: (candidate as { event: string }).event,
                    data: (candidate as { data?: unknown }).data,
                });
            }
        }
        return frames;
    }
}
